import type { DatabasePageSpec, FigureSpec, SliceHeaderSpec } from './model.js';
import { isolationMatrixFixture } from './fixtures/isolation-matrix.js';
import { concurrencySequenceFixture } from './fixtures/concurrency-sequence.js';
import { memoryLayoutFixture } from './fixtures/memory-layout.js';
import { queryPlanFixture } from './fixtures/query-plan.js';
import { latencyDistributionFixture } from './fixtures/latency-distribution.js';
import { stackFramesFixture } from './fixtures/stack-frames.js';
import { complexityArgumentFixture } from './fixtures/complexity-argument.js';
import { executionArtifactFixture } from './fixtures/execution-artifact.js';
import { btreeFixture } from './fixtures/btree.js';
import { protocolStateMachineFixture } from './fixtures/protocol-state-machine.js';

export function sliceFixture(width = 640, stress = false, focus: SliceHeaderSpec['focus'] = 'overview'): SliceHeaderSpec {
  return {
    id: stress ? 'slice-header-stress' : 'slice-header',
    family: 'slice-header',
    view: 'exploded',
    viewport: { width, height: 430 },
    pointer: stress ? '0xc0000140a8' : '0xc0…a8',
    pointerAddress: '0xc0000140a8',
    wordBytes: 8,
    len: stress ? 5 : 4,
    cap: stress ? 8 : 6,
    targetIndex: stress ? 3 : 1,
    values: stress ? [100, 42, 7, 3, 999999, null, null, null] : [100, 42, 7, 3, null, null],
    focus,
    provenance: 'Go slice runtime model · fixture values intentionally explicit',
  };
}

export function pageFixture(width = 640, stress = false, focus: DatabasePageSpec['focus'] = 'overview'): DatabasePageSpec {
  return {
    id: stress ? 'database-page-stress' : 'database-page',
    family: 'database-page',
    view: 'section',
    viewport: { width, height: 440 },
    pageBytes: 8192,
    headerBytes: 24,
    slotBytes: 4,
    tuples: stress
      ? [
          { id: 'customer-001', label: 'customer:ada@example.dev', bytes: 612 },
          { id: 'customer-002', label: 'customer:grace@example.dev', bytes: 488 },
          { id: 'customer-003', label: 'customer:alan@example.dev', bytes: 704 },
          { id: 'customer-004', label: 'customer:barbara@example.dev', bytes: 536 },
          { id: 'customer-005', label: 'customer:edsger@example.dev', bytes: 660 },
          { id: 'customer-006', label: 'customer:margaret@example.dev', bytes: 420 },
        ]
      : [
          { id: 'customer-001', label: 'customer #001', bytes: 320 },
          { id: 'customer-002', label: 'customer #002', bytes: 488 },
          { id: 'customer-003', label: 'customer #003', bytes: 392 },
          { id: 'customer-004', label: 'customer #004', bytes: 544 },
        ],
    focus,
    provenance: 'PostgreSQL-style page anatomy · explicit illustrative tuple sizes',
  };
}

export function fixtureFor(family: FigureSpec['family'], width: number, stress: boolean, focus: string): FigureSpec {
  if (family === 'slice-header') return sliceFixture(width, stress, focus as SliceHeaderSpec['focus']);
  if (family === 'database-page') return pageFixture(width, stress, focus as DatabasePageSpec['focus']);
  if (family === 'isolation-matrix') {
    return isolationMatrixFixture(width, stress, focus === 'overview' ? undefined : focus);
  }
  if (family === 'concurrency-sequence') {
    const reveal = focus === 'overview' ? undefined : Number(focus);
    return concurrencySequenceFixture(width, stress, Number.isFinite(reveal) ? reveal : undefined);
  }
  if (family === 'memory-layout') {
    const mapped = stress
      ? focus === 'memory.segment.enabled' ? 'memory.segment.retry-policy-enabled' : focus === 'memory.segment.alignment-gap' ? 'memory.segment.cache-line-alignment-padding' : focus
      : focus;
    return memoryLayoutFixture(width, stress, mapped === 'overview' ? undefined : mapped);
  }
  if (family === 'query-plan') {
    const mapped = stress
      ? focus === 'scan-orders' ? 'scan-qualified-orders' : focus === 'join-customer' ? 'join-orders-customers' : focus === 'sum-revenue' ? 'group-customer-region' : focus
      : focus;
    return queryPlanFixture(width, stress, mapped === 'overview' ? undefined : mapped);
  }
  if (family === 'latency-distribution') return latencyDistributionFixture(width, stress, focus as 'overview' | 'body' | 'tail' | 'comparison');
  if (family === 'stack-frames') {
    const phase = focus === 'entry' || focus === 'return' ? focus : 'call';
    return stackFramesFixture(width, stress, phase);
  }
  if (family === 'complexity-argument') return complexityArgumentFixture(width, stress, focus === 'overview' ? undefined : focus);
  if (family === 'execution-artifact') {
    const mapped = stress && focus === 'artifact.line.execution-time' ? 'artifact.line.mallocgc' : focus;
    return executionArtifactFixture(width, stress, mapped === 'overview' ? undefined : mapped);
  }
  if (family === 'btree') {
    const mapped = stress && focus === 'leaf-d' ? 'leaf-21' : focus;
    return btreeFixture(width, stress, mapped === 'overview' ? undefined : mapped);
  }
  if (family === 'protocol-state-machine') return protocolStateMachineFixture(width, stress, focus);
  throw new Error('fixture: family is not registered');
}
